import type { HttpMethod, MethodAccessEntry } from "../../api/entitlementsAdminApi";

export type AccessLevel = "read" | "write" | "admin";
export type MethodAccessGridValue = Record<HttpMethod, AccessLevel | "">;

export const HTTP_METHODS: HttpMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];
export const ACCESS_LEVELS: AccessLevel[] = ["read", "write", "admin"];

export const defaultMethodAccess = (poType: string): MethodAccessGridValue => {
  if (poType === "API") {
    return { GET: "read", POST: "write", PUT: "write", PATCH: "write", DELETE: "admin" };
  }
  return { GET: "", POST: "", PUT: "", PATCH: "", DELETE: "" };
};

const DIGITS = /^\d+$/;
const PLACEHOLDER = /^\{.+\}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The path an API request is matched on (betm_common_utils `normalize_path`),
 * which is an exact lookup against PO_PATH with no prefix matching. Each run of
 * id segments (all digits, or a `{placeholder}`) becomes ONE empty segment, and
 * a YYYY-MM-DD segment becomes `{date}`. So `/assets/{id}` is stored as
 * `/assets/` and `/permission-object/{id}/method-access` as
 * `/permission-object//method-access`.
 */
export const normalizePoPath = (path: string): string => {
  const out: string[] = [];
  let inIdRun = false;
  (path || "").split("/").forEach((seg) => {
    if (DATE.test(seg)) {
      out.push("{date}");
      inIdRun = false;
    } else if (seg !== "{date}" && (DIGITS.test(seg) || PLACEHOLDER.test(seg))) {
      if (!inIdRun) out.push("");
      inIdRun = true;
    } else {
      out.push(seg);
      inIdRun = false;
    }
  });
  return out.join("/");
};

export interface PermissionObjectFilter {
  types?: string[];
  service?: string;
  enabled?: string;
  query?: string;
}

export const filterPermissionObjects = <T extends Record<string, any>>(
  list: T[],
  { types, service, enabled, query }: PermissionObjectFilter
): T[] => {
  const q = (query || "").trim().toLowerCase();
  return list.filter((po) => {
    if (types && types.length > 0) {
      if (!types.includes(po.po_type_code)) return false;
    } else if (po.po_type_code === "BLOB_FOLDER") {
      return false;
    }
    if (service && po.service_name !== service) return false;
    if (enabled && po.enabled !== enabled) return false;
    if (q) {
      const hay = `${po.permission_object_name || ""} ${po.po_path || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
};

export const toEntries = (grid: MethodAccessGridValue): MethodAccessEntry[] =>
  HTTP_METHODS.filter((m) => grid[m]).map((m) => ({
    http_method: m,
    access_level_name: grid[m],
  }));

export const fromEntries = (entries: MethodAccessEntry[]): MethodAccessGridValue => {
  const grid = defaultMethodAccess("UI_ELEMENT");
  entries.forEach((e) => {
    grid[e.http_method] = e.access_level_name as AccessLevel;
  });
  return grid;
};
